import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Classification } from '@docma-mm-core/classification/classification';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsClassificationFilterService implements OnDestroy {

  private currentProjectId: number;
  private classification1Subject = new BehaviorSubject<SelectItem[]>([]);
  private classification2Subject = new BehaviorSubject<SelectItem[]>([]);
  private classification3Subject = new BehaviorSubject<SelectItem[]>([]);
  private classification4Subject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();

  public classification1$ = this.classification1Subject.asObservable();
  public classification2$ = this.classification2Subject.asObservable();
  public classification3$ = this.classification3Subject.asObservable();
  public classification4$ = this.classification4Subject.asObservable();

  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadProjectData(project.MyDocmaUid);
      });
  }

  private async loadProjectData(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);

      await Promise.all([
        this.loadClassification1(db),
        this.loadClassification2(db),
        this.loadClassification3(db),
        this.loadClassification4(db)
      ]);
    } catch (error) {
      this.logger.error(error, '', 'FieldsClassificationFilterService');
    }
  }

  private async loadClassification1(db: any): Promise<void> {
    try {
      const classifications = await db.get(MmProjectDbCollections.classifications as never, 'Class1') as Classification[];
      const selectItems = this.formatClassificationsForDropdown(classifications || []);
      this.classification1Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsClassificationFilterService');
      this.classification1Subject.next([]);
    }
  }

  private async loadClassification2(db: any): Promise<void> {
    try {
      const classifications = await db.get(MmProjectDbCollections.classifications as never, 'Class2') as Classification[];
      const selectItems = this.formatClassificationsForDropdown(classifications || []);
      this.classification2Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsClassificationFilterService');
      this.classification2Subject.next([]);
    }
  }

  private async loadClassification3(db: any): Promise<void> {
    try {
      const classifications = await db.get(MmProjectDbCollections.classifications as never, 'Class3') as Classification[];
      const selectItems = this.formatClassificationsForDropdown(classifications || []);
      this.classification3Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsClassificationFilterService');
      this.classification3Subject.next([]);
    }
  }

  private async loadClassification4(db: any): Promise<void> {
    try {
      const classifications = await db.get(MmProjectDbCollections.classifications as never, 'Class4') as Classification[];
      const selectItems = this.formatClassificationsForDropdown(classifications || []);
      this.classification4Subject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsClassificationFilterService');
      this.classification4Subject.next([]);
    }
  }

  private formatClassificationsForDropdown(classifications: Classification[]): SelectItem[] {
    const selectItems: SelectItem[] = [];

    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });

    const sortedClassifications = classifications
      .filter(classification => classification.Id)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));

    sortedClassifications.forEach(classification => {
      const code = classification.Name1 || '';
      const description = classification.Name2 || '';

      let displayText: string;
      if (code && description) {
        displayText = `${code} - ${description}`;
      } else if (code) {
        displayText = code;
      } else if (description) {
        displayText = description;
      } else {
        this.logger.error('[formatClassificationsForDropdown] No display text found for classification: ' + classification?.Id, '', 'FieldsClassificationFilterService');
        return;
      }

      selectItems.push({
        id: classification.Id.toString(),
        text: displayText
      });
    });

    return selectItems;
  }

  public getClassification1DisplayNameById(classificationId: string): string {
    const classifications = this.classification1Subject.value;
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) {
      this.logger.error('[getClassification1DisplayNameById] No classification found for id: ' + classificationId, '', 'FieldsClassificationFilterService');
      throw new Error('[getClassification1DisplayNameById] No classification found for id: ' + classificationId);
    }
    return classification.text;
  }

  public getClassification2DisplayNameById(classificationId: string): string {
    const classifications = this.classification2Subject.value;
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) {
      this.logger.error('[getClassification2DisplayNameById] No classification found for id: ' + classificationId, '', 'FieldsClassificationFilterService');
      throw new Error('[getClassification2DisplayNameById] No classification found for id: ' + classificationId);
    }
    return classification.text;
  }

  public getClassification3DisplayNameById(classificationId: string): string {
    const classifications = this.classification3Subject.value;
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) {
      this.logger.error('[getClassification3DisplayNameById] No classification found for id: ' + classificationId, '', 'FieldsClassificationFilterService');
      throw new Error('[getClassification3DisplayNameById] No classification found for id: ' + classificationId);
    }
    return classification.text;
  }

  public getClassification4DisplayNameById(classificationId: string): string {
    const classifications = this.classification4Subject.value;
    const classification = classifications.find(c => c.id === classificationId);
    if (!classification) {
      this.logger.error('[getClassification4DisplayNameById] No classification found for id: ' + classificationId, '', 'FieldsClassificationFilterService');
      throw new Error('[getClassification4DisplayNameById] No classification found for id: ' + classificationId);
    }
    return classification.text;
  }
}
